import { useEffect, useCallback, useState } from "react";
import { ReactComponent as IconCloseGrey } from "@/assets/svgs/icon-trash-bin.svg";
import { ReactComponent as IconFileUpload } from "@/assets/svgs/etr-file-upload-Icon-Badge.svg";
import PropTypes from 'prop-types';
import Toast2 from '@/components/Toast2';
import { useDropzone } from 'react-dropzone'
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature";

const CellFileUploadComponent = ({
    schema,
    isReadOnly,
    payload,
    parentProps,
    inputProps,
    setInputProps,
    updatePayload,
    validateSchema,
    getFieldValue,
    defaultFileLists = []
}) => {

    const [schemaOptions] = useState(schema["x-uioptions"]);
    const [fileLists, setFileLists] = useState(defaultFileLists);

    const onDrop = useCallback(acceptedFiles => {
        const newFiles = acceptedFiles.map(file => Object.assign(file, {
            preview: URL.createObjectURL(file)
        }))

        setFileLists(oldFiles => {
            const checkedOldFiles = oldFiles?.filter(oldFile => (oldFile?.filename && oldFile?.file_content))?.filter(oldFileObj => !newFiles.find(newFile => newFile.name === oldFileObj.name));
            // Check if limits are defined
            const hasFileSizeLimit = typeof schemaOptions.allowedSizePerFileInMB === 'number';
            
            // Check file size limit (if defined, otherwise allow any size)
            if (hasFileSizeLimit && newFiles?.find(el => el.size > (schemaOptions.allowedSizePerFileInMB * 1000000))) {
                setError(`File uploaded size is more than ${schemaOptions.allowedSizePerFileInMB}MB`)
                return checkedOldFiles;
            }
            
            // If maxItems is defined, check the limit
            if (schemaOptions.maxItems && (newFiles?.length + checkedOldFiles?.length) > schemaOptions.maxItems) {
                setError(`You can only upload a maximum of ${schemaOptions.maxItems} files${hasFileSizeLimit ? ` (up to ${schemaOptions.allowedSizePerFileInMB}MB each)` : ''}`)
                return checkedOldFiles;
            }
            return checkedOldFiles.concat(newFiles);
        })
    }, []);

    const onDropRejected = useCallback((fileRejections) => {
        setError(`Invalid file format. Supported formats: ${schemaOptions?.allowedExtensions.join(', ')}.`);
    });

    const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: schemaOptions?.allowedExtensions, onDropRejected, })

    const [error, setError] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const attachmentsData = getFieldValue(payload, parentProps);
        if (feature(featureConstant.ETR_IGPNI)) {
            const newInputProps = {
                ...inputProps,
                disabled: isReadOnly,
                value: attachmentsData,
            };

            if (JSON.stringify(newInputProps) !== JSON.stringify(inputProps)) {
                console.log("change input props")
                setInputProps(newInputProps);
            }
        } else {
            setInputProps({
                ...inputProps,
                disabled: isReadOnly,
                value: attachmentsData,
            });
        }

        if (attachmentsData?.attachments) {
            if (feature(featureConstant.ETR_IGPNI)) {
                (JSON.stringify(attachmentsData.attachments) !== JSON.stringify(fileLists)) && setFileLists(attachmentsData?.attachments);
            } else {
                setFileLists(attachmentsData?.attachments);
            }
        }
    }, [payload]);

    useEffect(() => {
        if (Array.isArray(fileLists) && (feature(featureConstant.ETR_IGPNI) ? fileLists.length > 0 : true)) {

            //Check is nullable property
            const isNullable = Array.isArray(schema.type) ? schema.type.includes('null') : schema.type === 'null';

            const value = {
                "attachments": (isNullable && fileLists?.length === 0) ? null : fileLists
            }
            setInputProps({ ...inputProps, value });
            updatePayload(value);
            validateSchema(value);
        }
    }, [fileLists]);

    function removeUploadedFile(fileName) {
        const data = fileLists?.filter((item) => !item.name.includes(fileName));
        setFileLists(data);
        feature(featureConstant.ETR_IGPNI) && setRefreshKey((prev) => (prev + 1));
        feature(featureConstant.ETR_IGPNI) && updatePayload(data.length > 0 ? { attachments: data } : null);
    }

    function onAddAttachment(e) {
        e.stopPropagation();
        if (typeof schemaOptions?.maxItems !== 'number' || fileLists?.length < schemaOptions?.maxItems) {
            document.getElementById(`input-file-${parentProps[0]}`).click();
        }
    }

    function showFileFormatLists() {
        const list = schemaOptions?.allowedExtensions.map(element => {
            return element.toUpperCase();
        });
        return [list?.slice(0, -1).join(', '), list?.slice(-1)[0]].join(list?.length < 2 ? '' : ' or ');
    }

    return (
        <>
            <div className="eform-row" data-testid="eform-row" key={`${parentProps[0]}`} {...getRootProps()}>
                <div className="eform-attachments-drop" onClick={(e) => onAddAttachment(e)} data-testid="eform-attachments-drop">
                    + Drop your attachment here or <span className="eform-attachments-drop-browse">browse</span>.
                </div>
                <input
                    type="file"
                    accept={`${schemaOptions?.allowedExtensions.toString()}`}
                    className="hidden"
                    id={`input-file-${parentProps[0]}`}
                    data-testid="eform-input-fileupload"
                    key={feature(featureConstant.ETR_IGPNI) && refreshKey}
                    {...getInputProps()}
                ></input>
            </div>
            <div className="eform-file-upload-text">
                Supported Format: {showFileFormatLists()}{schemaOptions?.allowedSizePerFileInMB ? `. Maximum ${schemaOptions.allowedSizePerFileInMB}MB per file` : ''}
            </div>
            {fileLists?.length > 0 && <div className="eform-file-upload-attachments-lists">
                {fileLists?.map((file, index) => {
                    return (
                        file.name && <div className="eform-file-upload-attachment-data" key={index}>
                            {!feature(featureConstant.ETR_IGPNI) && <div className="eform-file-upload-attachments-heading">{file.name}</div>}
                            {feature(featureConstant.ETR_IGPNI) && <>
                                <div className="eform-file-upload-attachment-data-icon"><IconFileUpload /></div>
                                <div className="eform-file-upload-attachment-data-heading">
                                    <div className="eform-file-upload-attachment-data-heading-text">{file.name}</div>
                                </div>
                            </>}
                            <div className="tooltip">
                                <div className="eform-file-upload-attachment-data-icon-remove"><IconCloseGrey data-testid="eform-remove-file-icon" onClick={() => { removeUploadedFile(file.name) }} /></div>
                                <div className="tooltiptext eform-tooltip-text">
                                    {"Remove"}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>}
            <Toast2 show={error} errorMessage={error} title={"Invalid File"} warningIcon={true} close={() => { setError("") }} />
        </>
    );
};

CellFileUploadComponent.propTypes = {
    isReadOnly: PropTypes.any,
    payload: PropTypes.any,
    parentProps: PropTypes.any,
    inputProps: PropTypes.any,
    setInputProps: PropTypes.any,
    updatePayload: PropTypes.any,
    validateSchema: PropTypes.any,
    getFieldValue: PropTypes.any,
    schema: PropTypes.any,
    defaultFileLists: PropTypes.array
};

export default CellFileUploadComponent;
